import { Component, OnInit, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Button } from 'ustore-ui';

import { AuthService } from '../../core/services/auth.service';
import { LoginRequest, MobileLoginRequest } from '../../core/models/login.model';
import { ToastService } from '../../shared/services/toast.service';
import { UserFacade } from '../../core/store/user/user.facade';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { GtmService } from '../../core/services/gtm.service';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { CustomValidators } from '../../shared/validators';
import { RegisterService } from '../../core/services/register.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { environment } from '../../../environments/environment';
import { CampaignFlowType } from '../../core/campaign/campaign-contract';
import {
  readCampaignLoginFlowTypes,
  readCampaignPurchaseIntent,
} from '../../core/campaign/campaign-checkout-flag';

interface ClaroLoginOption {
  kind: 'MOVEL' | 'FIXA';
  labelKey: string;
  descriptionKey: string;
}


@Component({
  selector: 'umkt-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    Button,
    RouterModule,
    FooterComponent,
    AuthLayoutComponent,
    CustomInputComponent,
    I18nPipe
  ],
  templateUrl: './login.component.html',
  styleUrls: ['../../../assets/style/login-register.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  loading: boolean = false;
  error: string = '';
  loginAttempts: number = 0;
  maxLoginAttempts: number = 5;
  isBlocked: boolean = false;
  isMobileLogin: boolean = false;
  campaignFlowTypes: CampaignFlowType[] = [];
  showResendConfirmationLink = false;
  emailWithUnconfirmedRegistration: string = '';
  passwordWithUnconfirmedRegistration: string = '';
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private userFacade = inject(UserFacade);
  private gtm = inject(GtmService);
  private registerService = inject(RegisterService);
  private i18nService = inject(I18nService);
  private utmPreservationService = inject(UtmPreservationService);
  private channelEventsService = inject(ChannelEventsService);

  constructor() {
    this.userFacade.getIsLoading().subscribe(isLoading => {
      this.loading = isLoading;
    });
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, CustomValidators.emailWithDomainValidator()]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      remember: [false],
    });
    this.loadSavedCredentials();
    this.campaignFlowTypes = readCampaignLoginFlowTypes();
  }

  get showProspectLogin(): boolean {
    return this.allows('PROSPECT');
  }

  get showEmailForm(): boolean {
    return this.showProspectLogin || this.isMobileLogin;
  }

  get mobileBackGoesHome(): boolean {
    return !this.allows('PROSPECT') && !this.allows('FIXA');
  }

  get claroOptions(): ClaroLoginOption[] {
    if (this.isMobileLogin) {
      return [];
    }

    const present = new Set(this.campaignFlowTypes);
    const unrestricted =
      !present.size ||
      (present.has('PROSPECT') && present.has('MOVEL') && present.has('FIXA'));
    if (unrestricted && this.isUtmOrCanal) {
      return [];
    }

    const options: ClaroLoginOption[] = [];
    if (this.allows('MOVEL')) {
      options.push({
        kind: 'MOVEL',
        labelKey: 'pages.login.claroButton.text',
        descriptionKey: 'pages.login.claroButton.description',
      });
    }
    if (this.allows('FIXA')) {
      options.push({
        kind: 'FIXA',
        labelKey: 'pages.login.claroButton.textFixa',
        descriptionKey: 'pages.login.claroButton.descriptionFixa',
      });
    }
    return options;
  }

  get separatorBeforeClaro(): boolean {
    return this.showProspectLogin && !this.isMobileLogin && this.claroOptions.length === 1;
  }

  private allows(flow: CampaignFlowType): boolean {
    if (!this.campaignFlowTypes.length) {
      return true;
    }

    const present = new Set(this.campaignFlowTypes);
    const showsEveryFlow = present.has('PROSPECT') && present.has('MOVEL') && present.has('FIXA');
    return showsEveryFlow || present.has(flow);
  }

  chooseClaro(kind: ClaroLoginOption['kind']): void {
    if (kind === 'MOVEL') {
      this.loginWithClaro();
      return;
    }
    this.redirectToClaroFixaLogin();
  }

  login() {
    // Verificar se está bloqueado
    this.showResendConfirmationLink = false;
    this.emailWithUnconfirmedRegistration = '';
    this.passwordWithUnconfirmedRegistration = '';

    if (this.isBlocked) {
      return;
    }

    const formValue = this.loginForm.value;

    if (this.isMobileLogin) {
      const mobileLoginRequest: MobileLoginRequest = {
        login: formValue.email,
        password: formValue.password
      };

      this.authService.mobileLogin(mobileLoginRequest).subscribe({
        next: ({ mobileLoginResponse }) => {
          this.resetLoginAttempts();

          this.gtm.login('mobile', mobileLoginResponse.user.uuid);
          this.channelEventsService
            .sendUserLoginEvent({
              method: 'SUE',
              userUuid: mobileLoginResponse.user.uuid,
              login: formValue.email,
              email: mobileLoginResponse.user.email || formValue.email,
            })
            .subscribe();
          this.toastService.showSuccess(
            this.i18nService.instant('pages.login.messages.success'),
            3000,
            'top-center'
          );

          if (formValue.remember) {
            this.saveCredentials(formValue.email);
          } else {
            this.clearSavedCredentials();
          }

          const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
            ...(this.route.snapshot?.queryParams || {}),
          });
          if (readCampaignPurchaseIntent() && !mobileLoginResponse.isFirstAccess) {
            this.router.navigate(['/checkout'], { queryParams });
            return;
          }
          this.router.navigate(['/register-movel'], { queryParams });
        },
        error: error => {
          console.error('Erro no login móvel:', error);
          this.handleLoginError();

          let errorMessage = this.i18nService.instant('pages.login.messages.connectionError');

          if (error.status === 500 && error.error && error.error.errors) {
            const hasInvalidCredentials = error.error.errors.some((err: string) =>
              err.toLowerCase().includes('invalid credentials')
            );

            if (hasInvalidCredentials) {
              if (this.isBlocked) {
                errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentialsBlocked');
              } else {
                errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentials');
              }
              this.error = errorMessage;
              this.toastService.showError(errorMessage, 5000, 'top-center');
              return;
            }
          }

          if (error.status === 400 || error.status === 404) {
            if (this.isBlocked) {
              errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentialsBlocked');
            } else {
              errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentials');
            }
          }
          else if (error.status === 500) {
            errorMessage = this.i18nService.instant('pages.login.messages.genericConnectionError');
          }
          else if (error.error && error.error.msg) {
            errorMessage = this.isBlocked
              ? `${error.error.msg} ${this.i18nService.instant('pages.login.messages.tryAgainMinutes')}`
              : error.error.msg;
          }

          this.error = errorMessage;
          this.toastService.showError(errorMessage, 5000, 'top-center');
        }
      });
      return;
    }

    // Login PME Hub
    const loginRequest: LoginRequest = {
      login: formValue.email,
      password: formValue.password,
    };

    this.authService.login(loginRequest).subscribe({
      next: ({ loginResponse }) => {
        if (loginResponse.status === 'success') {
          // Reset tentativas em caso de sucesso
          this.resetLoginAttempts();

          this.gtm.login('email', loginResponse.data?.userUuid);
          this.channelEventsService
            .sendUserLoginEvent({
              method: 'LOGIN',
              userUuid: loginResponse.data?.userUuid,
              login: formValue.email,
              email: formValue.email,
            })
            .subscribe();
          this.toastService.showSuccess(
            this.i18nService.instant('pages.login.messages.success'),
            3000,
            'top-center'
          );

          if (formValue.remember) {
            this.saveCredentials(formValue.email);
          } else {
            this.clearSavedCredentials();
          }

          const redirectAfterLogin = typeof sessionStorage !== 'undefined' 
            ? sessionStorage.getItem('redirect_after_login') 
            : null;

          const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
            ...(this.route.snapshot?.queryParams || {}),
          });

          if (redirectAfterLogin) {
            sessionStorage.removeItem('redirect_after_login');
            this.router.navigate([redirectAfterLogin], { queryParams });
          } else if (readCampaignPurchaseIntent()) {
            this.router.navigate(['/checkout'], { queryParams });
          } else if (loginResponse.data?.isFirstAccess) {
            this.router.navigate(['/register-movel'], { queryParams });
          } else {
            this.router.navigate(['/'], { queryParams });
          }
        } else {
          this.handleLoginError();
          let errorMessage = loginResponse.msg || this.i18nService.instant('pages.login.messages.userNotFound');       
          if (this.isBlocked) {
            errorMessage = `${errorMessage} ${this.i18nService.instant('pages.login.messages.tryAgainMinutes')}`;
          }
          this.error = errorMessage;
          this.toastService.showError(errorMessage, 5000, 'top-center');
        }
      },
      error: error => {
        console.error('Erro no login:', error);

        if (error.status === 400 && error.error) {
          const errorMsg = (error.error.msg || '').toLowerCase();
          const errorStatus = error.error.status;

          if ((errorStatus === '403' || errorStatus === 403) &&
            (errorMsg.includes('user is sue') || errorMsg.includes('authentication failed'))) {
            
            const sueMessage = this.i18nService.instant('pages.login.messages.sueUserError');
            
            this.toastService.showError(sueMessage, 5000, 'top-center');
            this.error = sueMessage;
            return;
          }
        }

        if (error?.error && Array.isArray(error.error.errors)) {
          const needsConfirmRegistration = error.error.errors.some(
            (err: string) =>
              err.toLowerCase().includes('need to confirm the registration')
          );

          if (needsConfirmRegistration) {
            const errorMessage = this.i18nService.instant('pages.login.messages.registrationNotConfirmed');
            this.error = errorMessage;
            this.emailWithUnconfirmedRegistration = this.loginForm.get('email')?.value || '';
            this.passwordWithUnconfirmedRegistration = this.loginForm.get('password')?.value || '';
            this.showResendConfirmationLink = true;
            this.toastService.showError(errorMessage, 5000, 'top-center');
            return;
          }
        }

        this.handleLoginError();

        let errorMessage = this.i18nService.instant('pages.login.messages.connectionError');

        if (error.status === 400 || error.status === 404) {
          if (this.isBlocked) {
            errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentialsBlocked');
          } else {
            errorMessage = this.i18nService.instant('pages.login.messages.invalidCredentials');
          }
        }
        else if (error.status === 500) {
          errorMessage = this.i18nService.instant('pages.login.messages.genericConnectionError');
        }
        else if (error.error && error.error.msg) {
          errorMessage = this.isBlocked
            ? `${error.error.msg} ${this.i18nService.instant('pages.login.messages.tryAgainMinutes')}`
            : error.error.msg;
        }

        this.error = errorMessage;
        this.toastService.showError(errorMessage, 5000, 'top-center');
      }

    });
  }

  resendConfirmationEmailFromLogin(): void {
    const email = this.loginForm.get('email')?.value;

    if (!email) {
      this.toastService.showError(
        this.i18nService.instant('pages.login.messages.emailRequired'), 
        3000, 
        'top-center'
      );
      return;
    }

    this.showResendConfirmationLink = false;

    this.registerService.resendConfirmationEmail(email).subscribe({
      next: () => {
        this.toastService.showSuccess(
          this.i18nService.instant('pages.login.messages.resendSuccess'), 
          5000, 
          'top-center'
        );
      },
      error: (error) => {
        console.error('Erro ao reenviar email de confirmação:', error);
        this.toastService.showError(
          this.i18nService.instant('pages.login.messages.resendError'),
          5000,
          'top-center'
        );
      }
    });
  }


  private saveCredentials(email: string): void {
    localStorage.setItem('rememberedemail', email);
  }

  private loadSavedCredentials(): void {
    const savedemail = localStorage.getItem('rememberedemail');

    if (savedemail) {
      this.loginForm.patchValue({
        email: savedemail,
        remember: true,
      });
    }
  }

  private clearSavedCredentials(): void {
    localStorage.removeItem('rememberedemail');
  }

  private handleLoginError(): void {
    this.loginAttempts++;

    if (this.loginAttempts >= this.maxLoginAttempts) {
      this.isBlocked = true;
    }
  }

  private resetLoginAttempts(): void {
    this.loginAttempts = 0;
    this.isBlocked = false;
  }

  get emailControl() {
    return this.loginForm.get('email');
  }

  get passwordControl() {
    return this.loginForm.get('password');
  }

  get rememberControl() {
    return this.loginForm.get('remember');
  }

  get isLoginButtonDisabled(): boolean {
    return this.loginForm.invalid || this.loading || this.isBlocked;
  }

  get shouldShowResendButton(): boolean {
    const currentEmail = this.emailControl?.value || '';
    const currentPassword = this.passwordControl?.value || '';
    return this.showResendConfirmationLink &&
      !this.isMobileLogin &&
      currentEmail === this.emailWithUnconfirmedRegistration &&
      currentPassword === this.passwordWithUnconfirmedRegistration;
  }

  get isUtmOrCanal(): boolean {
    return this.utmPreservationService.isUtmEnabled();
  }

  getButtonText(): string {
    if (this.loading) {
      return this.i18nService.instant('shared.messages.loading');
    }
    if (this.isMobileLogin && !this.isUtmOrCanal) {
      return this.i18nService.instant('pages.login.mobileSubmit');
    }
    if (this.isBlocked) {
      return this.i18nService.instant('shared.buttons.login');
    }
    return this.i18nService.instant('shared.buttons.login');
  }

  redirectToClaroFixaLogin(): void {
    window.location.href = environment.claroFixaAuthUrl;
  }

  loginWithClaro(): void {
    const remember = this.loginForm.get('remember')?.value;
    const email = this.loginForm.get('email')?.value;

    if (remember && email) {
      this.saveCredentials(email);
    }

    this.isMobileLogin = true;

    if (!remember) {
      this.loginForm.patchValue({ email: '' });
    }
  }

  goBackToNormalLogin(): void {
    const remember = this.loginForm.get('remember')?.value;
    const email = this.loginForm.get('email')?.value;

    this.isMobileLogin = false;

    if (!remember) {
      this.loginForm.patchValue({ email: '', remember: false });
      this.clearSavedCredentials();
    } else {
      const emailToSave = email || localStorage.getItem('rememberedemail') || '';
      if (emailToSave) {
        this.saveCredentials(emailToSave);
        this.loginForm.patchValue({ email: emailToSave, remember: true });
      } else {
        this.loginForm.patchValue({ email: '' });
      }
    }
  }
}
